/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2024, Zumper
 * @description SpotlightListingCard
 */

import { Box, Flex } from '@chakra-ui/react'
import { selectLongTerm } from '@zcode-ui/filters'
import { ListingCardContactSection } from '@zcode-ui/web-zumper-features'
import { AnalyticsC, Origin, voidEvent } from '@zcode/nr-utils'
import { bookingProviders } from '@zcode/nr/es/shared/constants/source'
import * as Optimizely from '@zcode/nr/es/shared/packages/optimizely-middleware'
import {
  CLICK_LIST_CARD_1,
  CLICK_LIST_CARD_1_2_3,
  CLICK_LIST_CARD_2,
  CLICK_LIST_CARD_3,
} from '@zcode/nr/es/shared/thirdParty/optimizely/constants'
import type { Listable } from '@zcode/state-type'
import React, {
  memo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type MouseEventHandler,
} from 'react'
import { useInView } from 'react-intersection-observer'
import { useDispatch, useSelector } from 'react-redux'
import { selectShouldLimitLeads } from '../../../../message/MessageForm/helpers/getShouldLimitLeads'
import { useTrackImpression } from '../ListItem/hooks/useTrackImpression'
import { ListingCardMessageButton } from '../ListingCard/ListingCardContactSection/ListingCardMessageButton'
import { ListingCardTourButton } from '../ListingCard/ListingCardContactSection/ListingCardTourButton'
import { useOnPaidPhoneClick } from '../ListingCard/helpers/useOnPaidPhoneClick'
import { SpotlightListingCardContent } from '../SpotlightListingCard/SpotlightListingCardContent'
import { SpotlightListingImageSection } from './SpotlightListingImageSection'

export interface SpotlightListingCardProps {
  analyticChannel:
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
  entity: Listable
  forceIntersected?: boolean
  index: number
  isInitialLocationChanged: boolean
  isSimilarListings?: boolean
  onClick: (e: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => void
  onClickQuickLook: (e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) => void
  onClickThirdPartyListCard: () => void
  onMouseEnter: MouseEventHandler<HTMLDivElement>
  onMouseLeave: MouseEventHandler<HTMLDivElement>
  origin: Origin
  page: number
  shouldPreloadImage?: boolean
  trackListItemImpression: () => void
}

const eventKeys = [CLICK_LIST_CARD_1, CLICK_LIST_CARD_2, CLICK_LIST_CARD_3]

export const SpotlightListingCardLarge = memo(
  ({
    analyticChannel,
    entity,
    forceIntersected,
    index,
    isInitialLocationChanged,
    isSimilarListings,
    onClick,
    onClickQuickLook,
    onClickThirdPartyListCard,
    onMouseEnter,
    onMouseLeave,
    origin,
    page,
    shouldPreloadImage,
    trackListItemImpression,
  }: SpotlightListingCardProps) => {
    const [showInfo, setShowInfo] = useState(false)
    const dispatch = useDispatch()
    const [ref, inView] = useInView({ threshold: 0.95 })
    useTrackImpression({
      entity,
      index,
      inView,
      onImpression: trackListItemImpression,
    })

    const { feedName } = entity
    const shouldLimitLeads = useSelector(selectShouldLimitLeads)
    const onPaidPhoneClick = useOnPaidPhoneClick()
    const isLongTerm = useSelector(selectLongTerm)

    const providers: readonly string[] = bookingProviders
    const shouldOpenThirdParty = !!feedName && providers.includes(feedName) && !isLongTerm

    const handleQuickLookClick = (e: MouseEvent<HTMLButtonElement>) => {
      voidEvent(e)
      !showInfo && onClickQuickLook(e)
      setShowInfo(!showInfo)
    }

    const handleOnClick = (e: MouseEvent<HTMLDivElement>) => {
      if (index < eventKeys.length) {
        dispatch(Optimizely.track({ eventKey: eventKeys[index] }))
        dispatch(Optimizely.track({ eventKey: CLICK_LIST_CARD_1_2_3 }))
      }

      if (shouldOpenThirdParty && isInitialLocationChanged) {
        onClickThirdPartyListCard()
      } else {
        onClick(e)
      }
    }

    const handleDetailClick = (
      e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>
    ) => {
      e.stopPropagation()
      onClick(e)
    }

    return (
      <Flex
        borderRadius="24px"
        cursor="pointer"
        h="444px"
        onClick={handleOnClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={ref}
      >
        <Box<'div'>>
          <SpotlightListingImageSection
            entity={entity}
            forceIntersected={forceIntersected}
            isLargeSpotlight
            parentName={AnalyticsC.LIST_ITEM}
            shouldPreloadImage={shouldPreloadImage}
          />
        </Box>
        <Flex
          background="blue.900"
          flexDir="column"
          minW={{ desktop: '332px', lg: '286px', xl: '410px' }}
          position="relative"
          px={{ desktop: 2, xl: 4 }}
          pt="104px"
          w={{ desktop: '332px', lg: '286px', xl: '410px' }}
        >
          <SpotlightListingCardContent
            entity={entity}
            handleQuickLookClick={handleQuickLookClick}
            hideQuickLook={isSimilarListings}
            isLargeSpotlight
          />
          <ListingCardContactSection
            ListingCardMessageButton={ListingCardMessageButton}
            ListingCardTourButton={ListingCardTourButton}
            analyticChannel={analyticChannel}
            entity={entity}
            forceDesktop
            isLargeCard
            isLargeSpotlight
            isShortTerm={false}
            isSpotlightVariation
            onDetailClick={handleDetailClick}
            onPaidPhoneClick={onPaidPhoneClick}
            origin={origin}
            paidPhoneMixpanelOrigin={Origin.LISTABLES}
            shouldLimitLeads={shouldLimitLeads}
            showBorder={false}
          />
        </Flex>
      </Flex>
    )
  }
)
